import { useContext, useEffect, useRef, useState, type ChangeEvent } from 'react';
import sortBy from 'lodash/sortBy';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { analytics, api, type ApiError, noop, useOnClickOutside } from 'libs';
import { Button, Panel } from 'components/common';
import { SavedFilter } from 'components/navigation';
import SavedFilterContext from './SavedFilterContext';

interface SavedSearch {
  id: number;
  name: string;
  search: string;
}

interface SavedFilterPanelProps {
  endpoint: string;
  filter: { parse: (s: string) => any; stringify: (s: any) => string };
  onFilterLoad?: (search: any) => void;
}

const SavedFilterPanel = ({ endpoint, filter, onFilterLoad = noop }: SavedFilterPanelProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const { buttonAsideRef, buttonTopRef, onClose, savedFilterPanelOpen } = useContext(SavedFilterContext);
  const [name, setName] = useState('');
  const [saveFilterError, setSaveFilterError] = useState('');
  const [searches, setSearches] = useState<SavedSearch[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint);
        setSearches(data);
      } catch (error) {
        toast.error((error as ApiError).response?.data?.message || 'Failed to load filters');
      }
    })();
  }, [endpoint]);

  useEffect(() => {
    setSaveFilterError('');
  }, [location.search]);

  useOnClickOutside(onClose, [buttonAsideRef!, buttonTopRef!, panelRef]);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSaveFilterError('');
    setName(event.target.value);
  };

  const onRemove = async (searchId: number) => {
    try {
      await api.delete(`${endpoint}/${searchId}`);
      analytics.productSearch.savedFilters.delete();
      setSearches((prevSearches) => prevSearches.filter((search) => search.id !== searchId));
      toast.success('Saved filter removed successfully');
    } catch (error) {
      toast.error((error as ApiError).response?.data?.message || 'Failed to remove filter');
    }
  };

  const onSave = async () => {
    try {
      if (!location.search) {
        setSaveFilterError('Apply one or more filters before saving');
        return;
      }
      const { data } = await api.post(endpoint, { name, search: location.search });
      analytics.productSearch.savedFilters.create();
      setSearches((prevSearches) => sortBy([...prevSearches, data], 'name'));
      setName('');
      setSaveFilterError('');
      toast.success('Saved filter created successfully');
    } catch (error) {
      toast.error((error as ApiError).response?.data?.message || 'Failed to save filter');
    }
  };

  const onSearch = (searchString: string) => {
    const search = filter.parse(searchString);

    navigate({
      pathname: location.pathname,
      search: filter.stringify(search),
    });
    onFilterLoad(search);
    onClose();
  };

  return (
    <Panel isOpen={savedFilterPanelOpen} ref={panelRef} testId="saved-filter-panel">
      <Panel.Header equalXPadding onClose={onClose} testId="saved-filter-panel">
        <div className="fw-bold ms-auto text-1_1875">Saved Filters</div>
      </Panel.Header>
      <Panel.Body>
        <div className="saved-filters-form">
          <h3 className="saved-filters__heading">Add a Saved Filter</h3>
          <input
            className="form-control mb-2"
            data-testid="saved-filter-name-field"
            onChange={onNameChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onSave();
              }
            }}
            type="text"
            placeholder="Name"
            value={name}
          />
          <Button
            disabled={!name}
            onClick={onSave}
            testId="saved-filter-save-button"
            text="bold"
            variant="primary-dark"
            width="block"
          >
            Save filter
          </Button>
          {saveFilterError && (
            <div className="invalid-feedback d-block mt-2" data-testid="saved-filter-validation-error">
              {saveFilterError}
            </div>
          )}
        </div>
        <h3 className="saved-filters__heading">Saved Filters</h3>
        {searches.map((search) => (
          <SavedFilter key={`search-${search.id}`} search={search} onClick={onSearch} onRemove={onRemove} />
        ))}
      </Panel.Body>
    </Panel>
  );
};

export default SavedFilterPanel;
